import React, { useState, useEffect } from "react";
import { 
    Card, 
    Typography, 
    Grid, 
    Button, 
    Box, 
    IconButton,
    CircularProgress,
    Alert,
    Link
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import SyncIcon from '@mui/icons-material/Sync';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import axiosInstance from "../AxiosInstance";

export default function SynchronizationPage() {
    const [syncState, setSyncState] = useState({
        lastSync: null,
        nextSync: null,
        isSyncing: false
    });
    
    const [loading, setLoading] = useState(true);
    const [syncEnabled, setSyncEnabled] = useState(false);
    const [autoDisabledMessage, setAutoDisabledMessage] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchSyncStatus();
        
        const pollingInterval = setInterval(() => {
            fetchSyncStatus();
        }, 30000);
        
        return () => clearInterval(pollingInterval);
    }, []);
    
    const fetchSyncStatus = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/settings/syncstate');
            
            setSyncEnabled(response.data.isEnabled);
            setAutoDisabledMessage(response.data.autoDisabledMessage);
            
            setSyncState({
                lastSync: response.data.lastSyncUtc,
                nextSync: response.data.nextSyncUtc,
                isSyncing: response.data.syncInProgress
            });
        } catch (err) {
            if (err.response && err.response.status === 404) {
                setSyncEnabled(false);
            } else {
                setError("Failed to fetch synchronization status. Please try again later.");
                console.error("Error fetching sync state:", err);
            }
        } finally {
            setLoading(false);
        }
    };

    const formatRelativeTime = (dateString) => {
        if (!dateString) return "Never";
        
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = date - now;
        const diffSec = Math.round(diffMs / 1000);
        const diffMin = Math.round(diffSec / 60);
        const diffHour = Math.round(diffMin / 60);
        const diffDay = Math.round(diffHour / 24);

        if (diffMs < 0) {
            // Past
            if (diffSec > -60) return `${Math.abs(diffSec)} seconds ago`;
            if (diffMin > -60) return `${Math.abs(diffMin)} minutes ago`;
            if (diffHour > -24) return `${Math.abs(diffHour)} hours ago`;
            return `${Math.abs(diffDay)} days ago`;
        } else {
            // Future
            if (diffSec < 60) return `in ${diffSec} seconds`;
            if (diffMin < 60) return `in ${diffMin} minutes`;
            if (diffHour < 24) return `in ${diffHour} hours`;
            return `in ${diffDay} days`;
        }
    };

    const isSyncDue = (nextSyncDate) => {
        if (!nextSyncDate) return false;
        
        const now = new Date();
        const nextSync = new Date(nextSyncDate);
        return nextSync - now < 300000 && nextSync > now; // Within next 5 minutes
    };

    const handleSyncNow = async () => {
        setSyncState(prev => ({
            ...prev,
            isSyncing: true
        }));
        
        try {
            await axiosInstance.post('/settings/syncnow');
            await fetchSyncStatus();
        } catch (err) {
            console.error("Error syncing:", err);
            setError("Failed to sync. Please try again later.");
            
            setSyncState(prev => ({
                ...prev,
                isSyncing: false
            }));
        }
    };

    const renderSyncStatusCard = () => {
        const syncDue = isSyncDue(syncState.nextSync);
        
        return (
            <Card variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 2, minHeight: '160px' }}>
                <Box sx={{ mt: 2 }}>
                    {!syncState.isSyncing && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                    Last sync: {formatRelativeTime(syncState.lastSync)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <AccessTimeIcon 
                                    fontSize="small" 
                                    sx={{ 
                                        mr: 1, 
                                        color: syncDue ? 'warning.main' : 'text.secondary',
                                        animation: syncDue ? 'pulse 1.5s infinite' : 'none'
                                    }} 
                                />
                                <Typography 
                                    variant="body2"
                                    sx={{ color: syncDue ? 'warning.main' : 'text.secondary' }}
                                >
                                    Next sync: {formatRelativeTime(syncState.nextSync)}
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    minHeight: '40px' // Ensure consistent height for header section
                }}>
                    {syncState.isSyncing ? (
                        <Typography variant="body2" color="primary">
                            Sync in progress...
                        </Typography>
                    ) : (
                        <Box></Box> 
                    )}
                    <Box sx={{ minWidth: '100px', display: 'flex', justifyContent: 'flex-end' }}>
                        {syncState.isSyncing ? (
                            <CircularProgress size={24} />
                        ) : (
                            <Button 
                                variant="contained" 
                                size="small"
                                onClick={handleSyncNow}
                                startIcon={<SyncIcon />}
                                disabled={!syncEnabled}
                            >
                                Sync now
                            </Button>
                        )}
                    </Box>
                </Box>
            </Card>
        );
    };

    return (
        <Box className="component-container">
            <Typography variant="h5" sx={{ mb: 3 }}>Data Sync</Typography>
            
            {
            syncEnabled && 
            <Typography variant="body1" sx={{ mb: 3 }}>
                Data is being imported to AbakusIQ from external systems.
            </Typography>
            }
            
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
                    <CircularProgress />
                </Box>
            ) : !syncEnabled ? (
                <>  
                    {autoDisabledMessage && (
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            <Typography>Integration was automatically disabled:</Typography>
                            <Typography sx={{ fontWeight: 'bold', my: 1 }}>{autoDisabledMessage}</Typography>
                            <Typography>Enable the integration again after resolving the issue.</Typography>
                        </Alert>
                    )}

                    <Alert severity="info" sx={{ mb: 3 }}>
                        Data sync is not available. Set up a <Link component={RouterLink} to="/settings/integrations">third party integration</Link> first.
                    </Alert>
                </>
            ) : error ? (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            ) : (
                <Grid container justifyContent="center">
                    <Grid item xs={12} sm={10} md={8} lg={6}>
                        {renderSyncStatusCard()}
                    </Grid>
                </Grid>
            )}
            
            {/* Add CSS for animations */}
            <style jsx="true">{`
                @keyframes pulse {
                    0% { opacity: 0.6; }
                    50% { opacity: 1; }
                    100% { opacity: 0.6; }
                }
            `}</style>
        </Box>
    );
} 